import { useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
import t from "../locales/translation";
import Package from "../models/Package";
import PackageTypes from "./PackageTypes";
import { PaymentWidgetProps } from "../models/PropsInterface";
import PaymentWidgetSkeletonShape from "./PaymentWidgetSkeletonShape";
import SuccessProcess from "./SuccessProcess";
import { logOrderProductsView } from "../logging/helpers/commonLoggingFunctions";
import { ActionDimensions } from "../logging/dimensions/ActionDimensions";
import { DEFAULT_VALUE } from "../logging/types/LoggingTypes";
import { useLogging } from "../logging/hooks/useLogging";
import ComboTypes from "./ComboTypes";
import "../cssStyles/PaymentWidget.css";
import "../cssStyles/ModalBundle.css";
import "react-loading-skeleton/dist/skeleton.css";

const PaymentWidget = (props: PaymentWidgetProps): JSX.Element => {
  console.log("PaymentWidgetConsole");
  /*-----------------------------
   *           STATES
   * ----------------------------*/
  const [packages, setPackages] = useState<any>([]);
  const [matchingPackage, setMatchingPackage] = useState<any>(null);
  const [bundles, setBundles] = useState<any>([]);
  const [label, setLabel] = useState<string>("");
  const [packagesConfig, setPackagesConfig] = useState<any>([]);
  const [product, setProduct] = useState<any>([]);
  const [load, setLoad] = useState<boolean>(false);
  const [isSuccessProcess, setIsSuccessProcess] = useState<boolean>(false);

  /*----------------------------
   *           LOGGING
   *-----------------------------*/
  useLogging(
    props.language,
    props && props.loggingConfig ? props.loggingConfig : undefined
  );
  const [comboConfig, setComboConfig] = useState<any>([]);

  /*-----------------------------
   * REQUEST API FOR FETCHING THE
   *          PRODUCTS
   * ----------------------------*/
  const fetchProducts = async () => {
    return axios.post(
      `${props.api}/package/fetch/${props.modelId}`,
      {
        products: props.products,
      },
      {
        headers: {
          source: props.source,
          country: props.countryAbbreviation,
          [`enforce-language`]: props.language,
          [`Authorization`]: "Bearer " + props.token,
        },
      }
    );
  };
  const fetchCombo = async () => {
    return axios.get(`${props.api}/vas/bundle-package`, {
      params: {
        service: props.service,
        postId: props.modelId,
      },
      headers: {
        source: props.source,
        country: props.countryAbbreviation,
        [`enforce-language`]: props.language,
        [`Authorization`]: "Bearer " + props.token,
      },
    });
  };

  /*-----------------------------
   *      GET PACKAGE CONFIG
   * ----------------------------*/
  const fetchPackageConfig = (packages: { [key: string]: Array<Package> }) => {
    const packageNames = Object.keys(packages);
    return packageNames.map((value, index) => {
      if (packageNames.length - 1 === index)
        return { padding: "right", name: value };
      else if (index === 0) return { padding: "left", name: value };
      return { padding: "mid", name: value };
    });
  };

  const fetchFirstProduct = (packages: { [key: string]: Array<Package> }) => {
    if (matchingPackage) {
      return matchingPackage.group;
    }
    return "shop" in packages
      ? "shop"
      : "Turbo" in packages
      ? "Turbo"
      : Object.keys(packages)[0];
  };

  useEffect(() => {
    t.setLanguage(props.language);
  });

  useEffect(() => {
    setLoad(true);
    if (props?.isCombo) {
      fetchCombo()
        .then((response) => {
          if (response.data) setComboConfig(response.data);
          setLoad(false);
        })
        .catch((error: AxiosError) => {
          setLoad(false);
        });
    } else {
      fetchProducts()
        .then((response) => {
          let countryCode = "jo";
          if (response.data.packages)
            countryCode = Object.values(response.data.packages)[0][0][
              "countryCode"
            ];
          localStorage.setItem("countryCode", countryCode);
          const fetchedPackages = response.data.packages;
          const fetchedBundles = response.data.bundles;
          const firstProduct = fetchFirstProduct(fetchedPackages);
          setProduct(firstProduct);
          setPackages(fetchedPackages);
          if (props?.sku) {
            Object.keys(fetchedPackages).some((groupName) => {
              const foundPackage = fetchedPackages[groupName].find(
                (item) => item.name === props?.sku
              );
              if (foundPackage) {
                setMatchingPackage({
                  id: foundPackage.productSettingId,
                  group: groupName,
                  productAssignId: foundPackage.productAssignId,
                });
                return true;
              }
            });
          }
          setBundles(fetchedBundles);
          setLabel(
            props.language === "en"
              ? fetchedPackages[firstProduct][0].package["en"]
              : fetchedPackages[firstProduct][0].package["ar"]
          );

          setPackagesConfig(fetchPackageConfig(fetchedPackages));
          setLoad(false);
          logOrderProductsView();
        })
        .catch((error: AxiosError) => {
          setLoad(false);
          logOrderProductsView();
        });
    }
  }, []);

  useEffect(() => {
    const firstProduct = fetchFirstProduct(packages);
    setProduct(firstProduct);
  }, [matchingPackage]);

  return !isSuccessProcess ? (
    !load ? (
      props?.isCombo ? (
        <ComboTypes
          {...props}
          setIsSuccessProcess={setIsSuccessProcess}
          config={comboConfig}
        ></ComboTypes>
      ) : (
        <div
          className={`payment_wedget__box`}
          dir={props.language === "en" ? "ltr" : "rtl"}
        >
          <div className={`payment_wedget__row`}>
            <p
              className={`vas payment_wedget__bold_font payment_wedget__font-size`}
            >
              {label}
            </p>
          </div>
          <PackageTypes
            matchingPackage={matchingPackage}
            setIsSuccessProcess={setIsSuccessProcess}
            domain={props.domain}
            pos_source={props.pos_source}
            referral_url={props.referral_url}
            api={props.api}
            token={props.token}
            countryAbbreviation={props.countryAbbreviation}
            source={props.source}
            modelId={props.modelId}
            packageConfig={packagesConfig}
            setLabel={setLabel}
            product={product}
            bundles={bundles}
            packages={packages}
            language={props.language}
            loggingConfig={props.loggingConfig}
          />
        </div>
      )
    ) : (
      <PaymentWidgetSkeletonShape></PaymentWidgetSkeletonShape>
    )
  ) : (
    <SuccessProcess referral_url={props.referral_url} />
  );
};

export default PaymentWidget;
